import {
    faCircleExclamation,
    faEnvelope,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { array, number, object, string } from "yup";
import {
    ChannelTypeEnum,
    type NotificationChannel,
    type NotificationGroup,
} from "../../apiClient/generated";
import { useAccountsApiClient, useNotificationsApi } from "../../hooks";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";
import { Modal } from "../../ui/Modals";
import { ExternalEmailsModal } from "./ChannelSettings";

const createEditFormSchema = object({
    groupName: string().required(),
    people: array().of(number()),
    channels: array().of(number()),
}).test(
    "at-least-one-selected",
    "At least one person or channel must be selected.",
    (value) =>
        (value.people && value.people.length > 0) ||
        (value.channels && value.channels.length > 0),
);

interface CreateEditFormProps {
    company?: number;
    group?: NotificationGroup;
    peopleOptions: {
        id: number;
        label: string;
    }[];
    channelOptions: {
        id: number;
        label: string;
    }[];
    onFinish: () => void;
    onDelete?: () => void;
}

interface MemberSelectorProps {
    peopleOptions: { id: number; label: string }[];
    channelOptions: { id: number; label: string }[];
    owner?: number;
    selectedPeople: number[];
    selectedChannels: number[];
    setPeople: (ids: number[]) => void;
    setChannels: (ids: number[]) => void;
    triggerRef: React.RefObject<HTMLButtonElement>;
}

const getInitials = (person: NotificationGroup["people"][number]) => {
    const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
    return name
        ? name
              .split(/\s+/)
              .map((part) => part[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
        : person.email.slice(0, 2).toUpperCase();
};

const getEmailInitials = (email?: string | null) =>
    (email ?? "@").split("@")[0].slice(0, 2).toUpperCase();

const toggleItem = (
    id: number,
    selected: number[],
    onChange: (ids: number[]) => void,
) =>
    onChange(
        selected.includes(id)
            ? selected.filter((selectedId) => selectedId !== id)
            : [...selected, id],
    );

const MemberSelector = (props: MemberSelectorProps) => {
    // Handle adding new channels inside
    const queryClient = useQueryClient();
    const apiClient = useNotificationsApi();
    const [error, setError] = useState<string>();
    const addChannelMutation = useMutation({
        mutationFn: async (email: string) => {
            const normalizedEmail = email.trim().toLowerCase();
            // Reuse an existing channel instead of creating a duplicate
            const existingChannel = props.channelOptions.find(
                (channel) => channel.label.toLowerCase() === normalizedEmail,
            );
            if (existingChannel) {
                return existingChannel;
            }
            return await apiClient.notificationsChannelsCreate({
                notificationChannelRequest: {
                    owner: props.owner,
                    channelType: ChannelTypeEnum.Email,
                    email: normalizedEmail,
                    webhookUrl: "",
                },
            });
        },
        onSuccess: (channel) => {
            setSearch("");
            setError(undefined);
            if (!props.selectedChannels.includes(channel.id)) {
                props.setChannels([...props.selectedChannels, channel.id]);
            }
            queryClient.invalidateQueries({
                queryKey: ["channelQuery", props.owner],
            });
            queryClient.invalidateQueries({
                queryKey: ["notificationChannelsList", props.owner],
            });
            queryClient.invalidateQueries({
                queryKey: ["notificationGroup"],
            });
        },
        onError: async (error: any) => {
            try {
                const body = await error.response?.json();
                if (body?.email) {
                    setError(body.email);
                } else {
                    setError("Something went wrong. Please try again.");
                }
            } catch {
                setError("Something went wrong. Please try again.");
            }
        },
    });

    const [search, setSearch] = useState("");
    const searchTerm = search.trim().toLocaleLowerCase();
    const selectedPeople = props.peopleOptions.filter((person) =>
        props.selectedPeople.includes(person.id),
    );
    const selectedChannels = props.channelOptions.filter((channel) =>
        props.selectedChannels.includes(channel.id),
    );
    const filteredPeople = props.peopleOptions.filter((person) =>
        person.label.toLocaleLowerCase().includes(searchTerm),
    );
    const filteredChannels = props.channelOptions.filter((channel) =>
        channel.label.toLocaleLowerCase().includes(searchTerm),
    );

    return (
        <Popover className="relative text-sm">
            <div className="rounded-lg border border-slate-300 bg-white p-2 focus-within:border-ae-blue-900">
                <Popover.Button
                    ref={props.triggerRef}
                    id="notification-group-members"
                    type="button"
                    className="w-full border-0 bg-transparent px-2 py-2 text-left text-sm text-slate-400 shadow-none outline-none ring-0"
                >
                    Search or add an external email
                </Popover.Button>
                {(selectedPeople.length > 0 || selectedChannels.length > 0) && (
                    <div className="flex flex-wrap gap-2 border-t border-slate-100 px-2 pb-1 pt-3">
                        {selectedPeople.map((person) => (
                            <span
                                key={person.id}
                                className="flex items-center gap-2 rounded-full bg-indigo-50 py-1 pl-2 pr-2 text-sm font-semibold text-slate-800"
                            >
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-sm text-ae-blue-900">
                                    {person.label
                                        .split(" ")
                                        .map((part) => part[0])
                                        .join("")
                                        .slice(0, 2)}
                                </span>
                                {person.label.replace(/\s*\(.*/, "")}
                                <button
                                    type="button"
                                    aria-label={`Remove ${person.label}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        toggleItem(
                                            person.id,
                                            props.selectedPeople,
                                            props.setPeople,
                                        );
                                    }}
                                >
                                    <XMarkIcon className="h-4 w-4 text-slate-500" />
                                </button>
                            </span>
                        ))}
                        {selectedChannels.map((channel) => (
                            <span
                                key={channel.id}
                                className="flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1 pl-2 pr-2 text-sm font-semibold text-slate-800"
                            >
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                                    @
                                </span>
                                {channel.label}
                                <button
                                    type="button"
                                    aria-label={`Remove ${channel.label}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        toggleItem(
                                            channel.id,
                                            props.selectedChannels,
                                            props.setChannels,
                                        );
                                    }}
                                >
                                    <XMarkIcon className="h-4 w-4 text-slate-500" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <Popover.Panel className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm text-slate-500">
                    <span>
                        {selectedPeople.length} people ·{" "}
                        {selectedChannels.length} external
                    </span>
                    <Popover.Button
                        type="button"
                        className="font-semibold text-ae-blue-900"
                    >
                        Done
                    </Popover.Button>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                    <input
                        aria-label="Search or add an external email"
                        autoFocus
                        type="email"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setError(undefined);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                // Prevent submitting the parent group form
                                event.preventDefault();
                            }
                        }}
                        placeholder="Search or add an external email"
                        className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-ae-blue-900"
                    />
                    {error && (
                        <div className="mb-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="mt-0.5 h-4 w-4 flex-shrink-0"
                            />
                            <span>{error}</span>
                        </div>
                    )}
                    {search && (
                        <button
                            type="button"
                            disabled={addChannelMutation.isPending}
                            onClick={() => addChannelMutation.mutate(search)}
                            className="flex w-full items-center gap-3 rounded-lg bg-slate-50 px-3 py-3 text-left hover:bg-slate-100 disabled:cursor-wait"
                        >
                            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-amber-700 bg-amber-50 text-amber-700">
                                <FontAwesomeIcon
                                    icon={faEnvelope}
                                    className="h-4 w-4"
                                />
                            </span>
                            <span>
                                <span className="block font-semibold">
                                    Invite {search}
                                </span>
                                <span className="block text-sm text-slate-500">
                                    External emails confirm by email before
                                    receiving alerts
                                </span>
                            </span>
                        </button>
                    )}
                    {filteredPeople.length > 0 && (
                        <>
                            <p className="px-2 pb-1 pt-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                                People
                            </p>
                            {filteredPeople.map((person) => (
                                <label
                                    key={person.id}
                                    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50"
                                >
                                    <input
                                        type="checkbox"
                                        checked={props.selectedPeople.includes(
                                            person.id,
                                        )}
                                        onChange={() =>
                                            toggleItem(
                                                person.id,
                                                props.selectedPeople,
                                                props.setPeople,
                                            )
                                        }
                                        className="h-4 w-4 rounded accent-ae-blue-900"
                                    />
                                    <span>{person.label}</span>
                                </label>
                            ))}
                        </>
                    )}
                    {filteredChannels.length > 0 && (
                        <>
                            <p className="px-2 pb-1 pt-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                                External emails and webhooks
                            </p>
                            {filteredChannels.map((channel) => (
                                <label
                                    key={channel.id}
                                    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50"
                                >
                                    <input
                                        type="checkbox"
                                        checked={props.selectedChannels.includes(
                                            channel.id,
                                        )}
                                        onChange={() =>
                                            toggleItem(
                                                channel.id,
                                                props.selectedChannels,
                                                props.setChannels,
                                            )
                                        }
                                        className="h-4 w-4 rounded accent-ae-blue-900"
                                    />
                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                                        @
                                    </span>
                                    <span>{channel.label}</span>
                                </label>
                            ))}
                        </>
                    )}
                    {searchTerm &&
                        filteredPeople.length === 0 &&
                        filteredChannels.length === 0 && (
                            <p className="px-2 py-3 text-sm text-slate-500">
                                No people or external emails match “
                                {search.trim()}”.
                            </p>
                        )}
                </div>
            </Popover.Panel>
        </Popover>
    );
};

const CreateEditForm = (props: CreateEditFormProps) => {
    const queryClient = useQueryClient();
    const apiClient = useNotificationsApi();
    const membersTriggerRef = useRef<HTMLButtonElement>(null);

    // Form and submit methods
    const form = useForm({
        defaultValues: {
            groupName: props.group?.name || "",
            people: props.group?.people.map((p) => p.id) || [],
            channels: props.group?.channels.map((c) => c.id) || [],
        },
        resolver: yupResolver(createEditFormSchema),
    });
    const selectedPeople = form.watch("people") ?? [];
    const selectedChannels = form.watch("channels") ?? [];
    const availableMembers =
        props.peopleOptions.length + props.channelOptions.length;

    const saveGroupMutation = useMutation({
        mutationFn: async (data: {
            groupName: string;
            people: number[];
            channels: number[];
        }) => {
            if (props.group) {
                return await apiClient.notificationsGroupsUpdate({
                    id: props.group.id,
                    notificationGroupCreateRequest: {
                        owner: props.company,
                        name: data.groupName,
                        channels: data.channels,
                        people: data.people,
                    },
                });
            }

            return await apiClient.notificationsGroupsCreate({
                notificationGroupCreateRequest: {
                    owner: props.company,
                    name: data.groupName,
                    channels: data.channels,
                    people: data.people,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["groupQuery"] });
            queryClient.invalidateQueries({
                queryKey: ["notificationGroup"],
            });
            queryClient.invalidateQueries({
                queryKey: ["emissionNotificationSettings"],
            });
            form.reset();
            props.onFinish();
        },
    });

    return (
        <form
            onSubmit={form.handleSubmit((data) =>
                saveGroupMutation.mutate({
                    groupName: data.groupName ?? "",
                    people: data.people ?? [],
                    channels: data.channels ?? [],
                }),
            )}
        >
            <div className="mt-6 text-sm">
                <div className="mb-6">
                    <label
                        htmlFor="notification-group-name"
                        className="mb-2 block text-sm font-semibold text-slate-600"
                    >
                        Group name
                    </label>
                    <input
                        id="notification-group-name"
                        type="text"
                        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none focus:border-ae-blue-900 focus:ring-1 focus:ring-ae-blue-900"
                        {...form.register("groupName")}
                    />
                </div>
                <div className="mb-6">
                    <div className="mb-2 flex items-center justify-between gap-4">
                        <div className="text-sm font-semibold text-slate-600">
                            Members
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-500">
                                {selectedPeople.length} people ·{" "}
                                {selectedChannels.length} external
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    membersTriggerRef.current?.click()
                                }
                                className="font-semibold text-ae-blue-900 hover:underline"
                            >
                                Browse all ({availableMembers})
                            </button>
                        </div>
                    </div>
                    <Controller
                        name="people"
                        control={form.control}
                        render={({ field: peopleField }) => (
                            <Controller
                                name="channels"
                                control={form.control}
                                render={({ field: channelsField }) => (
                                    <MemberSelector
                                        owner={props.company}
                                        peopleOptions={props.peopleOptions}
                                        channelOptions={props.channelOptions}
                                        selectedPeople={peopleField.value}
                                        selectedChannels={channelsField.value}
                                        setPeople={peopleField.onChange}
                                        setChannels={channelsField.onChange}
                                        triggerRef={membersTriggerRef}
                                    />
                                )}
                            />
                        )}
                    />
                </div>
            </div>
            {form.formState.errors[""]?.message && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <FontAwesomeIcon
                        icon={faCircleExclamation}
                        className="mt-0.5 h-4 w-4 flex-shrink-0"
                    />
                    <span>{form.formState.errors[""]?.message}</span>
                </div>
            )}
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
                {props.onDelete && (
                    <SecondaryButton type="button" onClick={props.onDelete}>
                        Delete group
                    </SecondaryButton>
                )}
                <div className="ml-auto flex gap-2">
                    <SecondaryButton type="button" onClick={props.onFinish}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        type="submit"
                        disabled={saveGroupMutation.isLoading}
                    >
                        {saveGroupMutation.isLoading
                            ? "Saving…"
                            : "Save changes"}
                    </PrimaryButton>
                </div>
            </div>
        </form>
    );
};

interface CreateEditModalProps {
    groupId?: number;
    company?: number;
    visible: boolean;
    onClose: () => void;
    onDelete?: () => void;
}

const CreateEditModal = (props: CreateEditModalProps) => {
    const apiClient = useNotificationsApi();
    const accountsApiClient = useAccountsApiClient();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const groupQuery = useQuery({
        queryKey: ["groupQuery", props.groupId, props.company],
        queryFn: async () => {
            return await apiClient.notificationsGroupsRetrieve({
                id: props.groupId,
            });
        },
        enabled: !!props.groupId,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const peopleQuery = useQuery({
        queryKey: ["peopleQuery", props.company],
        queryFn: async () => {
            const _people = await accountsApiClient.accountsUsersList({
                companymembershipCompany: props.company,
            });
            return _people.map((i) => {
                return {
                    id: i.id,
                    label: `${i.firstName} ${i.lastName} (${i.email})`,
                };
            });
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const channelQuery = useQuery({
        queryKey: ["channelQuery", props.company],
        queryFn: async () => {
            const _channels = await apiClient.notificationsChannelsList({
                owner: props.company,
                pageSize: 100,
            });
            return _channels.results.map((i) => {
                return {
                    id: i.id,
                    label: i.email || i.webhookUrl,
                };
            });
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const loading =
        (props.groupId && groupQuery.isLoading) ||
        channelQuery.isLoading ||
        peopleQuery.isLoading;

    return (
        <>
            <Modal
                size="md"
                visible={props.visible}
                onClose={() => {
                    if (!showDeleteConfirmation) {
                        props.onClose();
                    }
                }}
            >
                {props.groupId ? (
                    <h2 className="mt-2 text-xl font-semibold text-slate-900">
                        Edit group
                    </h2>
                ) : (
                    <h2 className="mt-2 text-xl font-semibold text-slate-900">
                        New group
                    </h2>
                )}
                {!loading && (
                    <CreateEditForm
                        company={props.company}
                        group={groupQuery.data}
                        peopleOptions={peopleQuery.data}
                        channelOptions={channelQuery.data}
                        onFinish={props.onClose}
                        onDelete={
                            props.groupId && props.onDelete
                                ? () => setShowDeleteConfirmation(true)
                                : undefined
                        }
                    />
                )}
            </Modal>
            <ConfirmationDialog
                visible={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={() => {
                    setShowDeleteConfirmation(false);
                    props.onDelete?.();
                }}
                title="Delete group?"
                message={<p>Are you sure you want to delete this group?</p>}
                confirmText="Delete"
            />
        </>
    );
};

interface NotificationGroupProps {
    company?: number;
    disabled?: boolean;
    isModalOpen: boolean;
    selectedGroup?: number;
    onEdit: (groupId: number) => void;
    onModalClose: () => void;
}

interface GroupRowProps {
    group: NotificationGroup;
    disabled?: boolean;
    onEdit: () => void;
}

const GroupRow = (props: GroupRowProps) => {
    const fallbackToEmails = props.group.people.length === 0;
    const members = fallbackToEmails
        ? props.group.channels
              .filter((channel) => channel.email)
              .map((channel) => ({
                  id: channel.id,
                  email: channel.email ?? "",
                  initials: getEmailInitials(channel.email),
              }))
        : props.group.people.map((person) => ({
              id: person.id,
              email: person.email,
              initials: getInitials(person),
          }));

    return (
        <div className="grid grid-cols-[minmax(14rem,1.3fr)_minmax(14rem,1.1fr)_minmax(14rem,1fr)_5rem] items-center gap-4 border-b border-slate-100 px-6 py-4 transition-colors hover:bg-slate-100 last:border-b-0">
            <h3 className="text-sm font-semibold text-ae-blue-900">
                {props.group.name}
            </h3>
            <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                    {members.slice(0, 4).map((member) => (
                        <span
                            key={member.id}
                            title={member.email}
                            className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-indigo-50 text-sm font-semibold text-ae-blue-900"
                        >
                            {member.initials}
                        </span>
                    ))}
                </div>
                <span className="text-sm text-slate-500">
                    {members.length}{" "}
                    {members.length === 1 ? "person" : "people"}
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {props.group.channels.map((channel) => (
                    <span
                        key={channel.id}
                        title={channel.email || channel.webhookUrl || undefined}
                        className="rounded-md bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-600"
                    >
                        {channel.email?.split("@")[0] || "Webhook"}
                    </span>
                ))}
            </div>
            {!props.disabled && (
                <button
                    type="button"
                    onClick={props.onEdit}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-ae-blue-900 hover:border-ae-blue-500"
                >
                    Edit
                </button>
            )}
        </div>
    );
};

export const NotificationGroupSettings = (props: NotificationGroupProps) => {
    const queryClient = useQueryClient();
    const apiClient = useNotificationsApi();
    const [showExternalEmailsModal, setShowExternalEmailsModal] =
        useState(false);

    const groupsQuery = useQuery({
        queryKey: ["notificationGroup", props.company],
        queryFn: async () =>
            await apiClient.notificationsGroupsList({
                owner: props.company,
                pageSize: 100,
            }),
    });

    const deleteGroupMutation = useMutation({
        mutationFn: async (id: number) =>
            await apiClient.notificationsGroupsDestroy({ id }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["notificationGroup"],
            });
            queryClient.invalidateQueries({
                queryKey: ["emissionNotificationSettings"],
            });
        },
    });

    const deleteRowFunction = async (id: number) =>
        await deleteGroupMutation.mutateAsync(id);
    const groups = groupsQuery.data?.results ?? [];
    const channelMap = new Map<number, NotificationChannel>();
    groups.forEach((group) =>
        group.channels.forEach((channel) => {
            if (channel.email) {
                channelMap.set(channel.id, channel);
            }
        }),
    );
    const channels = Array.from(channelMap.values());
    const pendingChannels = channels.filter(
        (channel) => !channel.active,
    ).length;

    return (
        <div>
            {groupsQuery.isLoading ? (
                <p className="px-6 py-4 text-sm text-slate-500">
                    Loading notification groups…
                </p>
            ) : groupsQuery.isError ? (
                <p className="px-6 py-4 text-sm text-red-700">
                    Unable to load notification groups.
                </p>
            ) : (
                <div>
                    {groups.map((group) => (
                        <GroupRow
                            key={group.id}
                            group={group}
                            disabled={props.disabled}
                            onEdit={() => props.onEdit(group.id)}
                        />
                    ))}
                    {groups.length === 0 && (
                        <p className="px-6 py-4 text-sm text-slate-500">
                            No notification groups yet.
                        </p>
                    )}
                    <div className="flex items-center justify-between gap-4 bg-slate-50 px-6 py-3 text-sm text-slate-600">
                        <div className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-amber-700 bg-amber-50 text-amber-700">
                                @
                            </span>
                            <span>
                                {channels.length} external emails across groups
                            </span>
                            {pendingChannels > 0 && (
                                <span className="rounded bg-amber-50 px-2 py-1 text-sm font-medium text-amber-800">
                                    {pendingChannels} pending
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowExternalEmailsModal(true)}
                            className="font-semibold text-ae-blue-900 hover:underline"
                        >
                            Manage
                        </button>
                    </div>
                </div>
            )}

            {/* Add modal */}
            <CreateEditModal
                company={props.company}
                groupId={props.selectedGroup}
                visible={props.isModalOpen}
                onClose={props.onModalClose}
                onDelete={
                    props.selectedGroup && !props.disabled
                        ? async () => {
                              await deleteRowFunction(props.selectedGroup!);
                              props.onModalClose();
                          }
                        : undefined
                }
            />
            <ExternalEmailsModal
                company={props.company}
                disabled={props.disabled}
                visible={showExternalEmailsModal}
                onClose={() => setShowExternalEmailsModal(false)}
            />
        </div>
    );
};
